import { AlertTriangle, Database, FileCheck2, GitBranch, Repeat2, UsersRound, Workflow } from 'lucide-react'
import type { FdeCopy, ModelElement, ProcessDataCoverage, ProcessDefinition, Relationship } from '@/frontend/features/Fde/types'

interface Props {
  processId: string
  definition: ProcessDefinition
  elements: ModelElement[]
  relationships: Relationship[]
  dataCoverage: ProcessDataCoverage
  copy: FdeCopy
  onOpenData: () => void
}

export default function ProcessContextSummary({ processId, definition, elements, relationships, dataCoverage, copy, onOpenData }: Props) {
  const relatedIds = new Set(relationships.flatMap((relationship) => {
    if (String(relationship.from.id) === processId) return [String(relationship.to.id)]
    if (String(relationship.to.id) === processId) return [String(relationship.from.id)]
    return []
  }))
  definition.nodes.flatMap((node) => node.ontology_refs).forEach((reference) => relatedIds.add(String(reference.element_id)))
  const related = elements.filter((element) => relatedIds.has(String(element.id)))
  const evidenceCount = new Set([
    ...definition.nodes.flatMap((node) => node.evidence_ids),
    ...definition.edges.flatMap((edge) => edge.evidence_ids),
  ]).size
  const items = [
    { icon: Workflow, label: copy.workspace.process_activities, value: definition.nodes.filter((node) => node.kind === 'activity').length },
    { icon: GitBranch, label: copy.workspace.process_branches, value: definition.edges.filter((edge) => ['conditional', 'exception', 'cancel'].includes(edge.transition_kind)).length },
    { icon: Repeat2, label: copy.workspace.process_returns, value: definition.edges.filter((edge) => ['return', 'retry'].includes(edge.transition_kind)).length },
    { icon: AlertTriangle, label: copy.workspace.process_gaps, value: definition.nodes.filter((node) => node.kind === 'gap').length },
    { icon: UsersRound, label: copy.workspace.process_people, value: related.filter((element) => element.ontology_class?.family.key === 'actors').length },
    { icon: Database, label: copy.workspace.process_systems, value: related.filter((element) => element.ontology_class?.family.key === 'technology').length },
    { icon: FileCheck2, label: copy.workspace.process_evidence_coverage, value: evidenceCount },
  ]

  return (
    <div className="flex h-10 shrink-0 items-center gap-4 overflow-x-auto border-b border-border bg-muted/10 px-4">
      {items.map(({ icon: Icon, label, value }) => <div key={label} className="flex shrink-0 items-center gap-1.5 text-[10px] text-muted-foreground"><Icon className="size-3" /><span>{label}</span><strong className="font-semibold tabular-nums text-foreground">{value}</strong></div>)}
      <button type="button" onClick={onOpenData} className="flex shrink-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground">
        <Database className="size-3" /><span>{copy.workspace.process_data_coverage}</span>
        <strong className={dataCoverage.summary.missing > 0 ? 'font-semibold tabular-nums text-amber-700' : 'font-semibold tabular-nums text-foreground'}>{dataCoverage.summary.mapped}/{dataCoverage.summary.requirements}</strong>
      </button>
      {definition.nodes.length > 1 && definition.edges.length === 0 && <div className="ml-auto flex shrink-0 items-center gap-1.5 rounded-full bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-800"><AlertTriangle className="size-3" />{copy.workspace.process_missing_connections}</div>}
    </div>
  )
}
